import React, { useEffect, useState } from 'react';
import { adminStats, adminSyncCatalog, adminUpdateSettings } from '../../lib/adminApi';
import { getSettings } from '../../lib/api';
import { Package, ShoppingBag, MessageSquare, TrendingUp, Boxes, RefreshCcw, Loader2, Power } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-5">
    <div className="flex items-center justify-between mb-3">
      <p className="text-zinc-500 text-xs uppercase tracking-wider">{label}</p>
      <Icon className={accent || 'text-[#909090]'} size={22} />
    </div>
    <p className="text-3xl font-bold text-white">{value}</p>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [maintenance, setMaintenance] = useState(null);
  const [togglingMaint, setTogglingMaint] = useState(false);

  const loadStats = () => {
    adminStats()
      .then(setStats)
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem('hh_admin_token');
          window.location.reload();
        }
      });
  };

  const loadSettings = () => {
    getSettings()
      .then((s) => setMaintenance(!!s.maintenance))
      .catch(() => setMaintenance(false));
  };

  useEffect(() => {
    loadStats();
    loadSettings();
  }, []);

  const handleToggleMaintenance = async () => {
    const next = !maintenance;
    const verb = next ? 'ENABLE' : 'DISABLE';
    const warn = next
      ? 'This will take the public storefront OFFLINE immediately. Every visitor will see the "We\'ll be right back" page. The Admin panel stays fully accessible.\n\nAre you sure?'
      : 'This will bring the public storefront back online. Continue?';
    if (!window.confirm(`${verb} maintenance mode?\n\n${warn}`)) return;
    setTogglingMaint(true);
    try {
      const res = await adminUpdateSettings({ maintenance: next });
      setMaintenance(!!res.maintenance);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to update maintenance mode');
    } finally {
      setTogglingMaint(false);
    }
  };

  const handleSync = async () => {
    if (!window.confirm(
      'This will sync product metadata (names, images, categories, tags, descriptions) from code.\n\n' +
      'IMPORTANT: Your manually edited PRICES, STOCK STATUS, and any extra products you added in this Admin will be PRESERVED — nothing you set in the Admin is overwritten.\n\n' +
      'Continue?'
    )) return;
    setSyncing(true);
    setSyncMessage('');
    try {
      const res = await adminSyncCatalog();
      setSyncMessage(
        `Synced — Products: ${res.products.updated} updated, ${res.products.inserted} newly inserted. ` +
        `Preserved fields: ${res.products.preserved_fields.join(', ')}. ` +
        `News: ${res.news.inserted} inserted (${res.news.removed} replaced).`
      );
      loadStats();
    } catch (err) {
      setSyncMessage(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-1">Dashboard</h1>
          <p className="text-zinc-500">Overview of your Hydro Health store.</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#909090] text-zinc-300 hover:text-white px-4 py-2 rounded text-sm transition-colors disabled:opacity-60"
        >
          {syncing ? <Loader2 className="animate-spin" size={16}/> : <RefreshCcw size={16}/>}
          Sync catalog from code
        </button>
      </div>

      {syncMessage && (
        <div className="mb-5 p-3 bg-[#909090]/10 border border-[#909090]/30 text-zinc-200 text-sm rounded">
          {syncMessage}
        </div>
      )}

      {/* Maintenance-mode control */}
      <div
        data-testid="maintenance-control"
        className={`mb-6 rounded-lg border p-5 flex items-center justify-between gap-4 flex-wrap ${
          maintenance
            ? 'bg-amber-500/10 border-amber-500/40'
            : 'bg-zinc-950 border-zinc-900'
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              maintenance ? 'bg-amber-500/20 text-amber-400' : 'bg-[#909090]/10 text-[#909090]'
            }`}
          >
            <Power size={18} strokeWidth={2} />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">
              Maintenance mode{' '}
              <span
                data-testid="maintenance-status"
                className={maintenance ? 'text-amber-400' : 'text-zinc-500'}
              >
                &middot; {maintenance === null ? '…' : maintenance ? 'ON' : 'OFF'}
              </span>
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              {maintenance
                ? 'The public storefront is currently offline. Only the Admin panel is reachable.'
                : 'Turn this on to take the storefront offline temporarily. Admin panel stays live.'}
            </p>
          </div>
        </div>
        <button
          data-testid="maintenance-toggle-btn"
          onClick={handleToggleMaintenance}
          disabled={togglingMaint || maintenance === null}
          className={`inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded text-sm transition-colors disabled:opacity-60 ${
            maintenance
              ? 'bg-amber-400 hover:bg-amber-300 text-black'
              : 'bg-[#909090] hover:bg-[#a8a8a8] text-black'
          }`}
        >
          {togglingMaint ? <Loader2 className="animate-spin" size={16} /> : <Power size={16} />}
          {maintenance ? 'Bring site back online' : 'Take site offline'}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
        <StatCard icon={Package} label="Products" value={stats?.products ?? '—'} />
        <StatCard icon={Boxes} label="In stock" value={stats?.inStock ?? '—'} />
        <StatCard icon={ShoppingBag} label="Orders" value={stats?.orders ?? '—'} />
        <StatCard icon={MessageSquare} label="Messages" value={stats?.contacts ?? '—'} />
        <StatCard icon={TrendingUp} label="Revenue" value={stats ? `€ ${stats.revenue.toFixed(2)}` : '—'} accent="text-green-400" />
      </div>

      <div className="mt-10 bg-zinc-950 border border-zinc-900 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-3">Quick start</h2>
        <ul className="space-y-2 text-sm text-zinc-400 list-disc list-inside">
          <li><span className="text-zinc-200 font-medium">Products</span> — add new peptides, edit prices, toggle stock</li>
          <li><span className="text-zinc-200 font-medium">Orders</span> — view incoming orders and update fulfilment stage</li>
          <li><span className="text-zinc-200 font-medium">News</span> — publish blog/news articles shown on the site</li>
          <li><span className="text-zinc-200 font-medium">Contacts</span> — read messages submitted through the contact form</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDashboard;
